import { useState, useMemo, useCallback } from "react";
import * as v0 from "@aidkitorg/types/lib/survey";
import { Button } from "@aidkitorg/component-library";
import { useModularMarkdown } from "../../Hooks/ModularMarkdown";
import { useLocalizedStrings } from "../../Localization";
import { DistroQuestionProps } from "../Props";
import { ConnectionModal } from "./ConnectionModal";

export function DeckVerification({
  component,
  info,
  setInfo,
}: DistroQuestionProps<v0.DeckVerification>) {
  const L = useLocalizedStrings();
  const [shouldShowConnectionModal, setShouldShowConnectionModal] = useState(false);
  const currentState = info[component.targetField] || "";

  const defaultText = L.questions.deck_verification;
  const header = useModularMarkdown({ content: component.header || defaultText.header, info });
  const questionDescription = useModularMarkdown({ content: component.questionDescription || "", info });
  const connectedDescription = useModularMarkdown({ content: component.connectedDescription || "", info });
  const optedOutDescription = useModularMarkdown({ content: component.optOutConfiguration.optedOutDescription || "", info });
  const verifyButtonText = useModularMarkdown({ content: component.verifyButtonText || defaultText.verify_button_text, info, noParagraphs: true });
  const reverifyButtonText = useModularMarkdown({ content: component.reverificationConfiguration.reverifyButtonText || defaultText.reverify_button_text, info, noParagraphs: true });
  const optOutButtonText = useModularMarkdown({ content: component.optOutConfiguration.optOutButtonText || defaultText.opt_out_button_text, info, noParagraphs: true });

  const completedDescription = useMemo(() => {
    switch (currentState) {
      case "pending":
      case "success":
      case "failed":
        return connectedDescription;
      case "opted_out":
        return optedOutDescription;
      default:
        return null;
    }
  }, [currentState, connectedDescription, optedOutDescription]);

  const primaryButtonText = ["pending", "success", "failed"].includes(currentState)
    ? reverifyButtonText
    : verifyButtonText;

  const handleClickVerify = useCallback(() => {
    setShouldShowConnectionModal(true);
  }, []);

  const handleClickOptOut = useCallback(() => {
    setInfo(component.targetField, "opted_out");
  }, [setInfo]);

  const handleSubmit = useCallback((jobGuid: string) => {
    setShouldShowConnectionModal(false);
    setInfo(component.targetField, "pending");
    setInfo(component.targetField + "_job_guid", jobGuid);
  }, [setInfo]);

  const handleExit = useCallback(() => {
    setShouldShowConnectionModal(false);
  }, []);

  return (
    <fieldset>
      <legend>
        {header}
        {questionDescription}
      </legend>
      <ConnectionModal
        open={shouldShowConnectionModal}
        component={component}
        onSubmit={handleSubmit}
        onExit={handleExit}
      />
      
      {(component.reverificationConfiguration.allowReverification || !currentState) &&
        // Applicants can enter the Deck flow if they haven't answered the question before or they're allowed to reverify
        <div className="mt-2">
          <Button
            variant="primary"
            onClick={handleClickVerify}
          >
            {primaryButtonText}
          </Button>
        </div>
      }

      {component.optOutConfiguration.allowOptOut && !currentState &&
        // Applicants can opt-out if they haven't answered this question before and they're allowed to opt-out
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={handleClickOptOut}
          >
            {optOutButtonText}
          </Button>
        </div>
      }

      {completedDescription && 
        <div className="mt-3">
          {completedDescription}
        </div>
      }
    </fieldset>
  );
};
